/**
 * Hook: useExportCsv — encapsula useMutation + descarga de blob CSV.
 * Cumple P-12: mientras isPending=true el caller debe deshabilitar el trigger
 * para garantizar idempotencia (no double-submit).
 *
 * @taskId T654
 * @see functional/Cycles/cycle-5/tasks.md HU-29-FE
 */

import { useMutation } from '@tanstack/react-query';

import { exportCsv, type ExportCsvParams, type ReportType } from '../api/reports.api';

function buildFilename(type: ReportType, from: string, to: string): string {
  return `reporte_${type.toLowerCase()}_${from}_${to}.csv`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface UseExportCsvReturn {
  /** Dispara la exportación. P-12: no llamar si isPending=true. */
  trigger: (params: ExportCsvParams) => void;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  reset: () => void;
}

export function useExportCsv(): UseExportCsvReturn {
  const mutation = useMutation({
    mutationFn: (params: ExportCsvParams) => exportCsv(params),
    onSuccess: (blob, variables) => {
      triggerBlobDownload(
        blob,
        buildFilename(variables.type, variables.from, variables.to),
      );
    },
  });

  return {
    trigger: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
